import { useMemo, useState } from "react";
import axios from "axios";
import { X, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";
import { proposerAppariements, SEUIL_CONFIANCE_PCT } from "../../lib/appariementLocuteurs";

/**
 * Tâche 1406 (Eric 2026-09-30) — La fenêtre de confirmation des attributions
 * proposées.
 *
 * Eric a demandé explicitement de pouvoir décocher avant d'appliquer : une
 * attribution fausse qui a l'air vraie contamine les connaissances et la
 * recherche par locuteur (acquis 1300). Chaque ligne porte donc son
 * pourcentage de temps commun, et le SECOND candidat quand il est proche —
 * c'est le seul signal qui dit « celle-là, vérifie-la ».
 */

const mmss = (s) => {
  const t = Math.max(0, Math.round(Number(s) || 0));
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

export function AppariementLocuteursModal({
  open,
  onClose,
  videoId,
  segmentsNommes,
  segmentsGeneriques,
  labelSourceNommee,
  labelSourceGenerique,
  onApplied,
}) {
  const { tr, lang } = useLang();
  const [decoches, setDecoches] = useState({});
  const [busy, setBusy] = useState(false);

  const lignes = useMemo(
    () => proposerAppariements(segmentsNommes, segmentsGeneriques),
    [segmentsNommes, segmentsGeneriques],
  );

  const estCoche = (l) => (l.generique in decoches ? decoches[l.generique] : l.pct >= SEUIL_CONFIANCE_PCT);
  const retenues = lignes.filter(estCoche);

  if (!open) return null;

  const appliquer = async () => {
    const mapping = {};
    retenues.forEach((l) => { mapping[l.generique] = l.propose; });
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/videos/${videoId}/speakers/validate`,
        { mapping },
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `${retenues.length} locuteur(s) attribué(s) depuis ${labelSourceNommee}.`
        : `${retenues.length} speaker(s) assigned from ${labelSourceNommee}.`);
      onApplied?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || tr("Attribution impossible", "Could not assign the speakers"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="appariement-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="appariement-modal"
      >
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-zinc-200">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-zinc-900" data-testid="appariement-titre">
              {tr("Attribuer les locuteurs automatiquement", "Assign speakers automatically")}
            </h3>
            <p className="text-xs text-zinc-600 mt-1" data-testid="appariement-explication">
              {lang === "fr"
                ? `Les noms viennent de ${labelSourceNommee}. Pour chaque locuteur anonyme de ${labelSourceGenerique}, on retient la personne qui parle EN MÊME TEMPS que lui le plus longtemps. Décochez ce qui vous semble douteux avant d'appliquer.`
                : `The names come from ${labelSourceNommee}. For each anonymous speaker in ${labelSourceGenerique}, we keep the person who speaks AT THE SAME TIME for the longest. Untick anything that looks doubtful before applying.`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 shrink-0"
            aria-label={tr("Fermer", "Close")}
            data-testid="appariement-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-auto flex-1 p-5 space-y-2">
          {lignes.length === 0 && (
            <p className="text-sm text-zinc-500" data-testid="appariement-vide">
              {tr(
                "Aucun recoupement de temps de parole entre les deux sources : rien ne peut être proposé sans inventer.",
                "No speaking-time overlap between the two sources: nothing can be proposed without guessing.",
              )}
            </p>
          )}
          {lignes.map((l) => {
            const doute = l.concurrent && l.pct - l.concurrentPct < 15;
            return (
              <label
                key={l.generique}
                className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                  estCoche(l) ? "border-[#002FA7]/40 bg-[#002FA7]/5" : "border-zinc-200 bg-white"
                }`}
                data-testid={`appariement-ligne-${l.generique}`}
                data-pct={l.pct}
                data-coche={estCoche(l) ? "true" : "false"}
              >
                <input
                  type="checkbox"
                  checked={estCoche(l)}
                  onChange={(e) => setDecoches((d) => ({ ...d, [l.generique]: e.target.checked }))}
                  className="mt-0.5"
                  data-testid={`appariement-case-${l.generique}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="text-sm text-zinc-900">
                    <span className="font-mono">{l.generique}</span>
                    <span className="mx-1.5 text-zinc-400">→</span>
                    <span className="font-semibold">{l.propose}</span>
                  </span>
                  <span className="block text-[11px] text-zinc-600 mt-0.5">
                    {lang === "fr"
                      ? `${l.pct} % de son temps de parole en commun (${mmss(l.secondesCommunes)} sur ${mmss(l.secondesGenerique)})`
                      : `${l.pct}% of its speaking time in common (${mmss(l.secondesCommunes)} of ${mmss(l.secondesGenerique)})`}
                  </span>
                  {doute && (
                    <span
                      className="mt-1 inline-flex items-start gap-1 text-[11px] text-amber-800"
                      data-testid={`appariement-doute-${l.generique}`}
                    >
                      <Warning size={12} className="mt-0.5 shrink-0" weight="fill" />
                      {lang === "fr"
                        ? `À vérifier : ${l.concurrent} est presque aussi proche (${l.concurrentPct} %).`
                        : `Worth checking: ${l.concurrent} is almost as close (${l.concurrentPct}%).`}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-zinc-200">
          <span className="text-[11px] text-zinc-500" data-testid="appariement-compte">
            {lang === "fr"
              ? `${retenues.length} sur ${lignes.length} seront appliqués. Les autres gardent leur libellé anonyme.`
              : `${retenues.length} of ${lignes.length} will be applied. The rest keep their anonymous label.`}
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-zinc-600 hover:text-zinc-900"
              data-testid="appariement-annuler"
            >
              {tr("Annuler", "Cancel")}
            </button>
            <button
              type="button"
              onClick={appliquer}
              disabled={busy || retenues.length === 0}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#002FA7] text-white disabled:opacity-40"
              data-testid="appariement-appliquer"
            >
              {busy ? tr("Attribution…", "Assigning…") : tr("Appliquer", "Apply")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppariementLocuteursModal;
